// =============================================================================
// MeuDriver — Admin Exports: Exportação CSV
// =============================================================================

import { useState } from 'react'
import { Download, FileSpreadsheet, Users, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useSEO } from '@/hooks/useSEO'
import { useExportBookingsCsv, useExportUsersCsv } from '@/hooks/useAdminExports'

export default function AdminExports() {
  useSEO({ title: 'Exportar Dados', noIndex: true })

  const today = new Date().toISOString().slice(0, 10)
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .slice(0, 10)

  const [startDate, setStartDate] = useState(firstOfMonth)
  const [endDate, setEndDate] = useState(today)

  const exportBookings = useExportBookingsCsv()
  const exportUsers = useExportUsersCsv()

  return (
    <div className="space-y-6 p-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Exportar dados</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Baixe relatórios em CSV para análise externa ou contabilidade.
        </p>
      </div>

      {/* Exportar Bookings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-brand-600" />
            Exportar Bookings (Aulas)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Exporta todas as aulas do período selecionado: aluno, instrutor, data, status, valor, taxa e repasse.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Data inicial</label>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="export-start-date"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Data final</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="export-end-date"
              />
            </div>
          </div>

          <Button
            onClick={() => exportBookings.mutate({ startDate, endDate })}
            disabled={exportBookings.isPending || !startDate || !endDate}
            className="gap-2"
            data-testid="export-bookings-btn"
          >
            <Download className="h-4 w-4" />
            {exportBookings.isPending ? 'Exportando...' : 'Exportar Bookings (CSV)'}
          </Button>
        </CardContent>
      </Card>

      {/* Exportar Usuários */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-600" />
            Exportar Usuários
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Exporta todos os usuários cadastrados: nome, e-mail, role, status e total de bookings.
          </p>

          <Button
            variant="outline"
            onClick={() => exportUsers.mutate()}
            disabled={exportUsers.isPending}
            className="gap-2 text-teal-700 border-teal-200 hover:bg-teal-50"
            data-testid="export-users-btn"
          >
            <Download className="h-4 w-4" />
            {exportUsers.isPending ? 'Exportando...' : 'Exportar Usuários (CSV)'}
          </Button>
        </CardContent>
      </Card>

      {/* Dicas */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 space-y-1">
        <p className="font-semibold flex items-center gap-1.5">
          <Calendar className="h-4 w-4" />
          Dicas de uso
        </p>
        <ul className="list-disc list-inside space-y-0.5 text-amber-700 text-xs ml-1">
          <li>O CSV usa ponto-e-vírgula como separador e BOM UTF-8 para compatibilidade com Excel.</li>
          <li>Para análise mensal, defina início e fim do mês desejado.</li>
          <li>E-mails de usuários ficam visíveis apenas para admins autenticados.</li>
        </ul>
      </div>
    </div>
  )
}
